import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { PhoneIcon, CloseIcon, MenuIcon } from '../../utils/IconWrapper';
import Button from '../ui/Button';
import LanguageSwitcher from '../ui/LanguageSwitcher';
import { useLanguage } from '../../i18n/LanguageContext';

const HeaderContainer = styled(motion.header)<{ $scrolled: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  transition: all 0.4s ease;
  background-color: ${({ $scrolled }) => ($scrolled ? 'var(--bg-primary)' : 'transparent')};
  box-shadow: ${({ $scrolled }) => ($scrolled ? '0 5px 15px rgba(0, 0, 0, 0.5)' : 'none')};
  height: 70px;
  display: flex;
  align-items: center;
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const Logo = styled(motion.div)`
  a {
    color: var(--text-primary);
    text-decoration: none;
    display: flex;
    align-items: center;
    gap: 1rem;
  }
  
  img {
    height: 75px;
    width: auto;
  }
  
  .logo-text {
    font-family: 'Playfair Display', serif;
    font-size: 1.4rem;
    font-weight: 600;
    color: #0d9488;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    text-shadow: 0 0 5px rgba(13, 148, 136, 0.3);
    transition: text-shadow 0.3s ease;
    
    &:hover {
      text-shadow: 0 0 8px rgba(13, 148, 136, 0.5);
    }
    
    @media (max-width: 768px) {
      font-size: 1.1rem;
    }
    
    @media (max-width: 480px) {
      display: none;
    }
  }
`;

const Nav = styled(motion.nav)`
  display: flex;
  align-items: center;
  
  @media (max-width: 992px) {
    display: none;
  }
`;

const NavItems = styled(motion.ul)`
  display: flex;
  list-style: none;
  margin-right: 2rem;
`;

const NavItem = styled(motion.li)`
  margin: 0 1rem;
  
  a {
    color: var(--text-primary);
    text-decoration: none;
    font-size: 0.9rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.05rem;
    padding: 0.5rem 0;
    position: relative;
    
    &:after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 0;
      height: 2px;
      background-color: var(--accent-primary);
      transition: width 0.3s ease;
    }
    
    &.active, &:hover {
      color: var(--accent-primary);
      
      &:after {
        width: 100%;
      }
    }
  }
`;

const NavActions = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const MobileMenuButton = styled(motion.button)`
  display: none;
  background: none;
  border: none;
  color: var(--text-primary);
  font-size: 1.5rem;
  cursor: pointer;
  
  @media (max-width: 992px) {
    display: block;
  }
`;

const MobileMenu = styled(motion.div)<{ $isOpen: boolean }>`
  position: fixed;
  top: 70px;
  right: ${({ $isOpen }) => ($isOpen ? '0' : '-100%')};
  width: 300px;
  height: calc(100vh - 70px);
  background-color: var(--bg-secondary);
  transition: right 0.3s ease;
  z-index: 999;
  padding: 2rem;
  
  @media (min-width: 993px) {
    display: none;
  }
`;

const MobileNavItems = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const MobileNavItem = styled(motion.li)`
  margin: 1.5rem 0;
  
  a {
    color: var(--text-primary);
    text-decoration: none;
    font-size: 1.1rem;
    font-weight: 500;
    display: block;
    
    &.active, &:hover {
      color: var(--accent-primary);
    }
  }
`;

const MobileLanguageSwitcher = styled.div`
  margin-top: 2rem;
  display: flex;
  justify-content: center;
`;

const Overlay = styled(motion.div)<{ $isOpen: boolean }>`
  position: fixed;
  top: 70px;
  left: 0;
  width: 100%;
  height: calc(100vh - 70px);
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 998;
  display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
  
  @media (min-width: 993px) {
    display: none;
  }
`;

const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { t } = useLanguage();
  
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };
  
  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };
  
  // Varianti per le animazioni
  const navItems = [
    { path: '/', label: t('nav.home') },
    { path: '/about', label: t('nav.about') }
  ];
  
  const mobileMenuVariants = {
    closed: { x: 300, opacity: 0 },
    open: { 
      x: 0, 
      opacity: 1,
      transition: {
        duration: 0.5,
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };
  
  const mobileNavItemVariants = {
    closed: { opacity: 0, x: 20 },
    open: { opacity: 1, x: 0 }
  };
  
  return (
    <>
      <HeaderContainer 
        $scrolled={isScrolled}
        initial={{ y: -70 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        <HeaderContent>
          <Logo
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Link to="/">
              <img src="/logo.svg" alt="J-O-H Logo" />
              <span className="logo-text">Short Rentals Network</span>
            </Link>
          </Logo>
          
          <Nav
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <NavItems>
              {navItems.map((item, i) => (
                <NavItem
                  key={item.path}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ 
                    duration: 0.5, 
                    ease: "easeOut",
                    delay: i * 0.1 
                  }}
                >
                  <Link 
                    to={item.path} 
                    className={location.pathname === item.path ? 'active' : ''}
                  >
                    {item.label}
                  </Link>
                </NavItem>
              ))}
            </NavItems>
            
            <NavActions>
              <LanguageSwitcher />

              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.6 }}
              >
                <Button
                  variant="outline"
                  size="small"
                  to="/contact"
                  icon={<PhoneIcon />}
                >
                  {t('nav.book')}
                </Button>
              </motion.div>
            </NavActions>
          </Nav>
          
          <MobileMenuButton 
            onClick={toggleMobileMenu}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            whileTap={{ scale: 0.9 }}
          >
            {isMobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
          </MobileMenuButton>
        </HeaderContent>
      </HeaderContainer>
      
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <MobileMenu 
              $isOpen={isMobileMenuOpen} 
              initial="closed"
              animate="open"
              exit="closed"
              variants={mobileMenuVariants}
            >
              <MobileNavItems>
                {navItems.map((item, i) => (
                  <MobileNavItem 
                    key={item.path}
                    variants={mobileNavItemVariants}
                  >
                    <Link 
                      to={item.path} 
                      className={location.pathname === item.path ? 'active' : ''}
                      onClick={closeMobileMenu}
                    >
                      {item.label}
                    </Link>
                  </MobileNavItem>
                ))}
              </MobileNavItems>
              
              <MobileLanguageSwitcher>
                <LanguageSwitcher />
              </MobileLanguageSwitcher>
              
              
              <motion.div
                variants={mobileNavItemVariants}
              >
                <Button
                  variant="primary"
                  size="medium"
                  fullWidth
                  to="/contact"
                  icon={<PhoneIcon />}
                  style={{ marginTop: '2rem' }}
                >
                  {t('nav.book')}
                </Button>
              </motion.div>
            </MobileMenu>
            
            <Overlay 
              $isOpen={isMobileMenuOpen}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeMobileMenu}
            />
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header; 